import React from 'react'
import TalentAppStep from "../components/TalentAppStep";
import FlipMove from "react-flip-move";
import LoadingBanner from "../../../components/LoadingBanner";
import User from "../../../models/User";
import ErrorBanner from "../../../components/ErrorBanner";
import EmptyBanner from "../../../components/EmptyBanner";
import { Checkbox } from "antd";
import { FormInputComponent } from "../../../components/form/Form";
import NEWSkillFormButton from '../../profiles/components/NEWSkillFormButton';

export default class RecommendedSkillsStep extends TalentAppStep {
    static path = '/recommended-skills';
    static LastUpdatedMethod = 'lastUpdatedWorkPreferences';

    /**
     * The title shown on the form
     */
    // static StepTitle = TB.trans('talent.recommended_skills.step_title');
    static StepTitle = 'Skill Optimizer';
    /**
     * The subtitle shown on the form
     */
    static StepSubtitle = TB.trans('talent.recommended_skills.step_subtitle');

    /**
     * The start page info tile
     */
    static StepInfo = TB.trans('talent.recommended_skills.step_info');

    /**
     * Show or hide review step info
     */
    static HideReview = true
    /**
     * The Step Section to be grouped in after application approval
     */
    static StepSection = 'Expertise';

    /**
     * The step needed before editing
     */
    static StepNeedsValidation(user) {
        if (!user.skills?.length) {
            return this;
        }
        if (!user.isApplicationApproved?.() && user.shouldRequirePortfolioLink?.()
            && !user.portfolio_url) {
            return this;
        }
        return false;
    };

    /**
     * Check if we should prevent application from saving and show alert
     */
    static async getValidProps(user) {
        return user.validateProfileOptimizer()
    };

    static hasCompleted(user) {
        let items = TB.pick(user, ['talent_disciplines', 'skills', 'talent_service_groups']);

        items = TB.mapValues(items, itemValues => TB.filter(itemValues, item => TB.isDefined(item?.id)));

        return items.talent_disciplines?.length > 0 && items.skills?.length > 0 && user.validateProfileOptimizer();
    }

    constructor(props) {
        super(props);

        this.state = {
            ...this.state,
            isLoading: true
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.fetchRecommendedSkills();
    }

    async onChange(params) {
        let model = {}
        for (let [key, value] of Object.entries(params)) {
            model = TB.set(model, key, value)
        }

        if (!model.portfolio_url_has_notes && this.props.user.portfolio_url_has_notes && model.portfolio_url_notes?.length) {
            model.portfolio_url_notes = null;
        }
        return super.onChange({ ...model });

    }

    async fetchRecommendedSkills() {
        await this.promisedState({ error: undefined, isLoading: true });

        let recommendedSkills = await (this.props.user).fetchTalentRecommendedSkills();

        if (!recommendedSkills) {
            return this.handleErrorResponse(
                User.FetchTalentRecommendedSkillsRequest
            );
        }

        this.promisedState({ recommendedSkills, isLoading: false });
    }

    async selectSkill(model) {

        let user = TB.pick({ ...this.props.user }, 'skills');
        let skillIndex = TB.findIndex(user.skills, ['id', model.id]);

        if (skillIndex !== -1) {
            if (!TB.isNil(model.self_score)) {
                user = TB.set(user, `skills[${skillIndex}]`, model);
            } else {
                TB.unset(user, `skills[${skillIndex}]`);
                // console.log('unset')
            }
        } else {
            user.skills.push({ ...model })
        }

        user.skills = TB.filter(user.skills, s => !TB.isNil(s?.id))

        await this.props.onChange(user);
    }

    missingRecommendedSkills() {
        if (!this.state.recommendedSkills) return;

        let userSkills = TB.map(this.props.user.skills, 'id');

        return TB.filter(this.state.recommendedSkills, ({ id }) => !userSkills.includes(id));
    }

    matchingRecommendedSkills() {
        if (!this.state.recommendedSkills) return;

        let keySkills = TB.map(this.state.recommendedSkills, 'id');

        return TB.filter(this.props.user.skills, ({ id }) => keySkills.includes(id));
    }

    /**
     * The fields mapping for the form
     */
    formFields = () => {

        let keySkills = this.matchingRecommendedSkills();
        let recommendedSkills = this.missingRecommendedSkills();
        const shouldRequirePortfolioLink = this.props.user.shouldRequirePortfolioLink()
        let {
            talent_disciplines = [],
            talent_service_groups = []
        } = this.props.user;
        let formFields = [
            {
                fields: {
                    spacer: (
                        <div style={{ flexBasis: '100%' }}>
                            <h5 className="form-header">Skill Optimizer</h5>
                            <p className="form-subheader" dangerouslySetInnerHTML={{
                                __html: TB.trans('talent.recommended_skills.step_subtitle', {
                                    discipline_count: talent_disciplines.length,
                                    service_count: talent_service_groups.length
                                })
                            }} />
                        </div>
                    )
                }
            },
        ];

        if (keySkills?.length) {
            formFields.push({
                style: styles.fieldSection,
                fields: {
                    skills: {
                        component: (
                            <div className={"form-group"} style={styles.field}>
                                <div className="form-title-section">
                                    <div className="step-title-content p-0">
                                        <div>
                                            <h3 className="form-title">
                                                My Key Skills Chosen
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="discipline-skills-container gap-3">
                                    <FlipMove typeName={null} maintainContainerHeight={true}>
                                        {keySkills.map((skill, index) => (
                                            <div key={`skill-${index}-${skill.id}`}>
                                                <NEWSkillFormButton
                                                    disabled={this.props.disabled}
                                                    skill={skill}
                                                    scoringOptions={skill.category ? laravel.talent_scoring_options_by_category[skill.category] : laravel.talent_scoring_options}
                                                    type={skill.category ? 'info' : 'default'}
                                                    onChange={(model) => this.selectSkill(model)}
                                                />
                                            </div>
                                        ))}
                                    </FlipMove>
                                </div>
                            </div>
                        ),
                    },
                }
            })
        }

        formFields.push({
            style: styles.fieldSection,
            fields: {
                recommended_skills: {
                    component: (
                        <div className="form-group mt-3" style={styles.field}>


                            {!this.state.isLoading && TB.isObject(this.state.error) && (
                                <ErrorBanner {...this.state.error}
                                    onRetry={() => this.fetchRecommendedSkills()} />
                            )}
                            {this.state.isLoading && <LoadingBanner message="Loading recommended skills" />}

                            {!this.state.isLoading && !TB.isObject(this.state.error) && (
                                (recommendedSkills && recommendedSkills.length > 0) ? (
                                    <div>
                                        <div className="form-title-section">
                                            <div className="step-title-content p-0">
                                                <div>
                                                    <h3 className="form-title">
                                                        Important Skills Not Chosen
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="discipline-skills-container gap-3">

                                            <FlipMove typeName={null} maintainContainerHeight={false}>
                                                {recommendedSkills.map((skill, index) => (
                                                    <div key={`skill-${index}-${skill.id}`}>
                                                        <NEWSkillFormButton
                                                            disabled={this.props.disabled}
                                                            skill={skill}
                                                            scoringOptions={skill.category ? laravel.talent_scoring_options_by_category[skill.category] : laravel.talent_scoring_options}
                                                            type={skill.category ? 'info' : 'default'}
                                                            onChange={(model) => {
                                                                if (TB.isDefined(model.self_score)) {
                                                                    this.selectSkill(model)
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </FlipMove>

                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-4 text-center">
                                        <EmptyBanner
                                            className="min-h-36 h-auto"
                                            icon="fas fa-clipboard-check"
                                            message={(
                                                <span dangerouslySetInnerHTML={{
                                                    __html: TB.trans('talent.recommended_skills.no_recommended_skills', {
                                                        discipline_count: talent_disciplines.length,
                                                        service_count: talent_service_groups.length
                                                    })
                                                }} />
                                            )} />
                                    </div>
                                )
                            )}
                        </div>

                    ),
                },
            }
        })


        if (shouldRequirePortfolioLink) {
            formFields.push(
                {
                    fields: {
                        spacer: (
                            <div style={{ flexBasis: '100%' }}>
                                <h5 className="form-header">Creative Requirements</h5>
                                <p className="form-subheader whitespace-pre-line">
                                    For our creative talent, a link to a portfolio site is required.
                                    {!this.props.user.isApplicationApproved() && 'This must be added before you submit your application.'}
                                    &nbsp;You have signed up for the following Creative Role(s):&nbsp;
                                    {/*{'\n'}*/}
                                    <span className="font-semibold text-info">{TB.map(this.props.user.creativeRolesAdded(), 'name').join(', ')}</span>
                                </p>
                            </div>
                        )
                    }
                }, {
                style: styles.fieldSection,
                fields: {
                    portfolio_url: {
                        label: `Portfolio URL (Include https://)`,
                        placeholder: 'Portfolio URL',
                        type: 'url',
                        required: shouldRequirePortfolioLink,
                        maxLength: 400,
                        style: styles.field,
                        validationErrors: {
                            isDefaultRequiredValue: 'This field is required'
                        },
                        columns: 10
                    },
                }
            },
            )
            if (this.props.user.portfolio_url?.length) {
                formFields.push({
                    style: styles.fieldSection,
                    fields: {
                        portfolio_url_has_notes_component: {
                            style: styles.field,
                            component: (
                                <FormInputComponent
                                    type="antd-input"
                                    label={false}
                                    fieldName="portfolio_url_has_notes"
                                    disabled={this.props.isDisabled}
                                    value={this.props.user.portfolio_url_has_notes}
                                >
                                    <Checkbox
                                        checked={this.props.user.portfolio_url_has_notes}>
                                        Include access instructions if needed
                                    </Checkbox>
                                </FormInputComponent>
                            )
                        },
                    }
                },)
            }
            if (this.props.user.portfolio_url_has_notes) {
                formFields.push({
                    style: styles.fieldSection,
                    fields: {
                        portfolio_url_notes: {
                            label: false,
                            type: 'textarea',
                            maxLength: 500,
                            placeholder: 'Specify here if the link requires password or other access information',
                            style: styles.field,
                            required: false,
                            rows: 3,
                            columns: 10
                        },
                    }
                })
            }
        }

        return formFields;
    };

    renderTitle() {

        return (
            <div className="form-title-section">
                <div className="step-title-content">
                    <div>
                        <h3 className="form-title">
                            {/* {TB.trans('talent.recommended_skills.step_title')} */}
                            Skill Optimizer
                        </h3>

                    </div>
                </div>
            </div>
        )
    }
}

const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
};